import type { DepthChartCardId } from './depthChartPositions';
import type { DepthChartView } from './scholarshipFilterStorage';

/**
 * The field scales to fit the chart's width, text included, so it shows everything at any size
 * and only gets smaller. Below this width it would be too small to read (under half its design
 * size: a phone, or a very narrow window), so with no choice made the chart opens as a list of
 * the same cards instead. The coach can switch either way.
 */
export const FIELD_VIEW_MIN_WIDTH = 700;

/**
 * How far either side of {@link FIELD_VIEW_MIN_WIDTH} the automatic choice waits before switching.
 * A classic scrollbar is about 15px, so a band wider than that keeps one appearing or disappearing
 * from flipping the layout, which would change the height and bring the scrollbar back.
 */
export const FIELD_VIEW_HYSTERESIS = 20;

/**
 * The layout the width picks when the coach has made no choice. `previous` is what it picked last
 * time (null before the first measurement): once on the list the field waits for
 * min + hysteresis, and once on the field the list waits for below min - hysteresis.
 */
export const resolveAutoDepthChartView = (
  width: number,
  previous: DepthChartView | null,
): DepthChartView => {
  const threshold =
    previous === 'list'
      ? FIELD_VIEW_MIN_WIDTH + FIELD_VIEW_HYSTERESIS
      : previous === 'field'
        ? FIELD_VIEW_MIN_WIDTH - FIELD_VIEW_HYSTERESIS
        : FIELD_VIEW_MIN_WIDTH;
  return width < threshold ? 'list' : 'field';
};

/**
 * The coach's explicit choice wins; otherwise the width decides, with hysteresis against the
 * previous automatic choice. Unknown width keeps the previous choice, or the field.
 */
export const resolveDepthChartView = (
  chosen: DepthChartView | null,
  width: number | null,
  previousAuto: DepthChartView | null = null,
): DepthChartView =>
  chosen ?? (width == null ? previousAuto ?? 'field' : resolveAutoDepthChartView(width, previousAuto));

/** List order runs round the diamond the way a coach reads a lineup card, battery first. */
export const LIST_CARD_ORDER: readonly DepthChartCardId[] = [
  'C',
  '1B',
  '2B',
  'SS',
  '3B',
  'LF',
  'CF',
  'RF',
  'DH',
  'UTIL',
];
